import type {
    KeyboardEvent as ReactKeyboardEvent,
    LiHTMLAttributes,
    MouseEvent as ReactMouseEvent,
    PropsWithChildren,
    ElementType
} from "react";
import { Children, createRef, useCallback, useEffect } from "react";

import { css } from "@compiled/react";

import { DataEventProps } from "@/libraries/wpl/foundations/src";

import { Adg4TabFC, mapChild } from "./tab-helper";
import { useAdg4TabsContext } from "./tabs";

/**
 * @deprecated - Please use @/libraries/design/patterns/tabs instead
 */
export type Adg4TabProps = PropsWithChildren<{
    dataEventProps?: DataEventProps;
    tabIndex?: number;
    focus?: boolean;
    label?: string;
    as?: ElementType;
}> &
    LiHTMLAttributes<HTMLLIElement>;

const plainStyle = css({
    display: "flex",
    cursor: "pointer",
    border: "none",
    backgroundColor: "transparent",
    userSelect: "none"
});

/**
 * @deprecated - Please use @/libraries/design/patterns/tabs instead
 */
const Adg4Tab: Adg4TabFC<Adg4TabProps> = ({
    id,
    focus,
    tabIndex,
    dataEventProps,
    className,
    children,
    ...props
}: Adg4TabProps) => {
    const { goToTab, isCurrentTab, userSelected } = useAdg4TabsContext();
    const tabRef = createRef<HTMLLIElement>();

    const isTabSelected = isCurrentTab(tabIndex);

    useEffect(() => {
        if (!tabRef.current) {
            return;
        }

        // Focus the selected tab if needed
        if (isTabSelected && focus) {
            tabRef.current.focus();
        }

        if (isTabSelected && dataEventProps && userSelected) {
            tabRef.current.dispatchEvent(new Event("select"));
        }
    }, [tabRef, isTabSelected, focus, userSelected, dataEventProps]);

    const onClick = useCallback(
        (e: ReactMouseEvent<HTMLLIElement>) => {
            props.onClick?.(e);
            if (isTabSelected) {
                return;
            }

            goToTab(tabIndex, true);
        },
        [isTabSelected, tabIndex, goToTab, props]
    );

    const onKeyDown = (e: ReactKeyboardEvent) => {
        if (e.code === "Space" || e.code === "Enter") {
            goToTab(tabIndex, true);
            e.preventDefault();
        }
    };

    return (
        <li
            {...props}
            ref={tabRef}
            css={plainStyle}
            className={className}
            id={`tab${id}`}
            aria-selected={isTabSelected}
            aria-controls={`panel${id}`}
            onClick={onClick}
            onKeyDown={onKeyDown}
            tabIndex={isTabSelected ? 0 : undefined}
            // Tech debt that should be fixed. See ldr/0012-jsx-a11y-strict.md for details
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
            role="tab"
            {...dataEventProps}
        >
            {Children.map(children, (child) => {
                return mapChild(child, { isSelected: isTabSelected });
            })}
        </li>
    );
};
Adg4Tab.tabsRole = "Adg4Tab";

export { Adg4Tab };
